"use client";
import Button from "@/components/common/Button";
import Link from "next/link";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import * as formValidation from "@/utils/validations";

import {
  selectLoginStatus,
  selectLoginError,
  makeLogin,
} from "@/components/features/auth/authSlice";
import { useFormik } from "formik";
import { useRouter } from "next/navigation";
import Spinner from "@/components/Spinner";
import { useState } from "react";

const SignIn = () => {
  const dispatch = useAppDispatch();
  const loginError = useAppSelector(selectLoginError);
  const navigate = useRouter();
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validate: formValidation.validateLogin,
    onSubmit: async (values) => {
      setLoading(true);
      const result = await dispatch(makeLogin(values));
      if (!result.error)
        if (result?.payload?.isAdmin) {
          navigate.push("/admin");
        } else if (result?.payload?.isTrainingAccount) {
          navigate.push("/demo");
        } else {
          navigate.push("/dashboard");
        }
      setLoading(false);
    },
  });

  return (
    <div className="flex bg-slate-300/90 backdrop-blur-none flex-col justify-center px-6 py-4 lg:px-20 rounded-2xl border-b-2 border-slate-400">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6" onSubmit={formik.handleSubmit}>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Email address
            </label>
            <div className="mt-2">
              <input
                id="email"
                type="email"
                autoComplete="email"
                className="block w-full rounded-md border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email ? (
                <p className="text-sm text-red-800 font-normal">
                  {formik.errors.email}
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Password
              </label>
              <div className="text-sm">
                <Link
                  href="/auth/forgot"
                  className="font-semibold text-orange-600 hover:text-orange-500"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <div className="mt-2">
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className="block w-full rounded-md border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
              />
              {formik.touched.password && formik.errors.password ? (
                <p className="text-sm text-red-800 font-normal">
                  {formik.errors.password}
                </p>
              ) : null}
            </div>
          </div>

          <div>
            <Button
              name={
                loading ? <Spinner classes="!text-white text-sm" /> : "Sign in"
              }
              className="w-full !bg-orange-600 !text-slate-200 hover:!bg-orange-500"
            />
          </div>
          {loginError && (
            <div className="text-sm text-red-800 font-normal mt-2">
              {loginError}
            </div>
          )}
        </form>

        <p className="mt-10 text-center text-sm text-gray-500">
          Not a member?{" "}
          <Link
            href="/auth/signup"
            className="font-semibold leading-6 text-orange-600 hover:text-orange-500"
          >
            Create account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SignIn;
